import React from "react";
import { computed, observable } from "mobx";
import { observer } from "mobx-react";
import {
  withRouter,
  RouteComponentProps,
  generatePath,
} from "react-router-dom";
import { FormSpy } from "react-final-form";

import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";

import { Button } from "@material-ui/core";

import Page from "components/Layout/Page";
import LoadingButton from "components/LoadingButton";
import FormControl, { TextFieldControl } from "components/FormControl";
import SubmitButtonControl, {
  CancelButtonControl,
} from "components/FormControl/SubmitButtonControl";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import PublishStatus from "components/PublishStatus";
import SimpleField from "components/SimpleField";

import { SNACKBAR_TYPES } from "components/Snackbar";

import { lengthValidator } from "utils/validators";

import { PUBLISH_STATUSES } from "constants/models";

import ROUTES from "navigation/routes";

import {
  BackButtonWrapper,
  Container,
  ControlsContainer,
  ControlContainer,
  Column,
  GroupRow,
  Row,
  Divider,
  PublishStatusWrapper,
} from "./styles";
import QuizSectionStore from "stores/QuizSectionStore";

type LocationState = {
  editMode: boolean;
  readOnly?: boolean;
};

type Props = RouteComponentProps<{ sectionId: string }, {}, LocationState> &
  SnackbarContextProps & {};

@observer
class SectionDetails extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;
  @observable closeConfirmationIsOpened: boolean = false;
  @observable updateConfirmationIsOpened: boolean = false;
  @observable onAccept: () => void = () => {};

  get locationState() {
    return this.props.location.state;
  }

  get isEditMode() {
    return this.locationState?.editMode;
  }

  get isReadOnlyMode() {
    return this.locationState?.readOnly;
  }

  get isNewSection() {
    const { sectionId } = this.props.match.params;

    return sectionId === undefined;
  }

  get sectionId() {
    const { sectionId } = this.props.match.params;

    const parsedsectionId = Number.parseInt(sectionId);
    return typeof parsedsectionId === "number" ? parsedsectionId : undefined;
  }

  get backClickText() {
    return " Back to Sections";
  }

  get backClickPath() {
    return ROUTES.CONTENT_QUESTIONNAIRE_SECTION;
  }

  @computed get displayValues(): any {
    const formData = QuizSectionStore.details?.formData;

    return {
      ...formData,
    };
  }

  @computed get initialValues() {
    if (this.isNewSection) {
      return {};
    } else {
      return {
        ...QuizSectionStore.details?.formData,
      };
    }
  }

  @computed get sectionPublishStatus() {
    return QuizSectionStore.details?.publishStatus;
  }

  async componentDidMount() {
    const { history } = this.props;

    if (this.isNewSection) {
      history.replace({ state: { editMode: true } });
    } else {
      await QuizSectionStore.fetchSectionDetails(this.sectionId);
    }
  }

  componentWillUnmount() {
    QuizSectionStore.resetDetails();
  }

  closeConfirmationPopup = () => {
    this.closeConfirmationIsOpened = false;
  };

  onCloseForm = (onAccept: () => void) => {
    if (this.isPopupDirty) {
      this.closeConfirmationIsOpened = true;
      this.onAccept = onAccept;
    } else {
      onAccept();
    }
  };

  onBackClick = () => {
    const onAccept = () => this.props.history.push(this.backClickPath);

    if (this.isEditMode) {
      this.onCloseForm(onAccept);
    } else {
      onAccept();
    }
  };

  onCancelClick = (resetForm: () => void) => {
    if (this.isNewSection) {
      this.onBackClick();
    } else {
      const onAccept = () => {
        this.exitEditMode();
        this.closeConfirmationPopup();
        this.resetIsPopupDirty();
        resetForm();
      };
      this.onCloseForm(onAccept);
    }
  };

  onEditClick = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_QUESTIONNAIRE_SECTION_DETAILS, {
      sectionId: this.sectionId,
    });
    history.replace({ pathname: path, state: { editMode: true } });
  };

  exitEditMode = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_QUESTIONNAIRE_SECTION_DETAILS, {
      sectionId: this.sectionId,
    });
    history.replace({ pathname: path, state: { editMode: false } });
  };

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    this.closeUpdateConfirmation();
    this.submit(values);
  };

  onSubmit = (values: any) => {
    const shouldShowUpdateConfirmation =
      this.sectionPublishStatus &&
      [
        PUBLISH_STATUSES.PUBLISHED,
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
      ].includes(this.sectionPublishStatus);

    shouldShowUpdateConfirmation
      ? this.openUpdateConfirmation()
      : this.submit(values);
  };

  submit = async (values: any) => {
    const { setMessage, history } = this.props;
    const { id, title, code } = values;

    const data = {
      title: title?.trim(),
      code: code?.trim(),
    };

    try {
      if (this.isNewSection) {
        const id = await QuizSectionStore.createSection(data);
        if (id) {
          const path = generatePath(
            ROUTES.CONTENT_QUESTIONNAIRE_SECTION_DETAILS,
            {
              sectionId: id,
            },
          );
          history.push({ pathname: path, state: { editMode: false } });
          QuizSectionStore.fetchSectionDetails(id);
        }
      } else {
        const payload = { ...data, id };
        await QuizSectionStore.editSectionInfo(payload);

        this.exitEditMode();
        QuizSectionStore.fetchSectionDetails(id);
      }
      this.resetIsPopupDirty();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <Page
        title="Section Details"
        leftControls={
          this.sectionPublishStatus && (
            <PublishStatusWrapper>
              <PublishStatus status={this.sectionPublishStatus} />
            </PublishStatusWrapper>
          )
        }
        topControls={
          <BackButtonWrapper>
            <Button
              color="secondary"
              variant="text"
              startIcon={<KeyboardBackspaceIcon />}
              onClick={this.onBackClick}
            >
              {this.backClickText}
            </Button>
          </BackButtonWrapper>
        }
        isLoading={QuizSectionStore.isSectionLoading}
      >
        {this.closeConfirmationIsOpened && (
          <Popup onClose={this.closeConfirmationPopup} withoutExitButton>
            <ConfirmationDialog
              title="Discard Changes"
              text="Are you sure you would like to discard changes? All information will be lost."
              declineText="Cancel"
              acceptText="Discard"
              onDecline={this.closeConfirmationPopup}
              onAccept={this.onAccept}
            />
          </Popup>
        )}
        <FormControl
          onSubmit={this.onSubmit}
          initialValues={this.initialValues}
          keepDirtyOnReinitialize={this.isNewSection}
        >
          {this.updateConfirmationIsOpened && (
            <FormSpy subscription={{ values: true }}>
              {({ values }) => (
                <Popup onClose={this.closeUpdateConfirmation} withoutExitButton>
                  <ConfirmationDialog
                    title="Save Changes to Production"
                    text="All changes made will be published to production. Are you sure you want to proceed?"
                    acceptText="Proceed"
                    declineText="Cancel"
                    onAccept={this.confirmUpdate(values)}
                    onDecline={this.closeUpdateConfirmation}
                  />
                </Popup>
              )}
            </FormSpy>
          )}
          <FormSpy
            subscription={{ dirty: true }}
            onChange={this.onChangeIsPopupDirty}
          />
          <Container>
            <ControlsContainer>
              {this.isEditMode ? (
                <>
                  <ControlContainer>
                    <CancelButtonControl
                      render={this.renderCancelButton}
                      onClick={this.onCancelClick}
                    />
                  </ControlContainer>
                  <ControlContainer>
                    <SubmitButtonControl render={this.renderButton} />
                  </ControlContainer>
                </>
              ) : (
                !this.isReadOnlyMode && (
                  <ControlContainer>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={this.onEditClick}
                      fullWidth
                    >
                      Edit
                    </Button>
                  </ControlContainer>
                )
              )}
            </ControlsContainer>

            <GroupRow>
              <Column>
                <Row>
                  <Column>
                    {this.isEditMode ? (
                      <TextFieldControl
                        variant="outlined"
                        margin="none"
                        name="title"
                        label="Title"
                        validate={lengthValidator(64)}
                        fullWidth
                        required
                      />
                    ) : (
                      <SimpleField
                        label="Title"
                        value={this.displayValues?.title}
                      />
                    )}
                  </Column>
                  <Column>
                    {this.isEditMode ? (
                      <TextFieldControl
                        variant="outlined"
                        margin="none"
                        name="code"
                        label="Code"
                        validate={lengthValidator(32)}
                        fullWidth
                        required
                      />
                    ) : (
                      <SimpleField
                        label="code"
                        value={this.displayValues?.code}
                      />
                    )}
                  </Column>
                </Row>
              </Column>
            </GroupRow>
            <Divider />
          </Container>
        </FormControl>
      </Page>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={QuizSectionStore.isSectionManaging}
      fullWidth
    >
      {this.isNewSection ? "Add" : "Save"}
    </LoadingButton>
  );

  renderCancelButton = ({ onClick }: { onClick: () => void }) => (
    <Button variant="outlined" color="primary" onClick={onClick} fullWidth>
      Cancel
    </Button>
  );
}

export default withGlobalSnackbar(withRouter(SectionDetails));
